"use client";

import { useEffect, useMemo, useState } from "react";
import { leadStore, LeadItem, LeadNote, LeadStatus } from "@/lib/store";
import { Plus, Upload, Download, ChevronDown, ChevronUp, X, Trash2 } from "lucide-react";

const STATUSES: { value: LeadStatus; label: string; color: string }[] = [
  { value: "new", label: "New", color: "#a1a1aa" },
  { value: "contacted", label: "Contacted", color: "#3b82f6" },
  { value: "followup", label: "Follow-up", color: "#06b6d4" },
  { value: "responded", label: "Responded", color: "#eab308" },
  { value: "meeting_booked", label: "Meeting Booked", color: "#a855f7" },
  { value: "proposal_sent", label: "Proposal Sent", color: "#f97316" },
  { value: "hold", label: "Hold", color: "#78716c" },
  { value: "won", label: "Won", color: "#22c55e" },
  { value: "lost", label: "Lost", color: "#ef4444" },
];

const COLUMNS: { key: keyof LeadItem; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "status", label: "Status" },
  { key: "lastContacted", label: "Last Contacted" },
  { key: "notes", label: "Notes" },
];

const emptyLead: Omit<LeadItem, "id" | "createdAt" | "updatedAt"> = {
  name: "",
  title: "",
  company: "",
  website: "",
  linkedin: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  status: "new",
  notes: "",
  lastContacted: "",
};

export function LeadsCRM() {
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<keyof LeadItem>("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [editing, setEditing] = useState<LeadItem | null>(null);
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyLead);
  const [loading, setLoading] = useState(true);

  const loadLeads = async () => {
    setLoading(true);
    const rows = await leadStore.list({ status: statusFilter === "all" ? undefined : statusFilter, search: search || undefined });
    setLeads(rows);
    setLoading(false);
  };

  useEffect(() => {
    loadLeads();
  }, [statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadLeads();
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const sorted = useMemo(() => {
    const copy = [...leads];
    copy.sort((a, b) => {
      const av = String(a[sortBy] ?? "").toLowerCase();
      const bv = String(b[sortBy] ?? "").toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return copy;
  }, [leads, sortBy, sortDir]);

  const counts = useMemo(() => {
    const tally: Record<string, number> = { total: leads.length };
    for (const s of STATUSES) tally[s.value] = 0;
    for (const l of leads) tally[l.status] = (tally[l.status] || 0) + 1;
    return tally;
  }, [leads]);

  const onSort = (key: keyof LeadItem) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const openEdit = async (lead: LeadItem) => {
    setEditing({ ...lead });
    const rows = await leadStore.notes(lead.id);
    setNotes(rows);
  };

  const saveEdit = async () => {
    if (!editing) return;
    await leadStore.update(editing.id, editing);
    setEditing(null);
    await loadLeads();
  };

  const addNote = async () => {
    if (!editing || !newNote.trim()) return;
    await leadStore.addNote(editing.id, newNote.trim());
    setNewNote("");
    setNotes(await leadStore.notes(editing.id));
  };

  const handleStatusQuick = async (id: number, status: LeadStatus) => {
    await leadStore.update(id, { status });
    await loadLeads();
  };

  const createLead = async () => {
    if (!createForm.name.trim() && !createForm.company.trim()) return;
    await leadStore.create(createForm);
    setCreateForm(emptyLead);
    setShowCreate(false);
    await loadLeads();
  };

  const importCsv = async (file?: File) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    await fetch("/api/leads/import", { method: "POST", body: form });
    await loadLeads();
  };

  const exportCsv = () => {
    const headers = COLUMNS.map((c) => c.label).join(",");
    const rows = sorted.map((l) => [
      l.name,
      l.title,
      l.company,
      l.linkedin,
      l.email,
      l.phone,
      l.city,
      l.state,
      statusLabel(l.status),
      l.lastContacted,
      l.notes,
    ]);

    const csv = [headers, ...rows.map((r) => r.map(escapeCsv).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <h2 className="text-2xl font-bold">Leads CRM</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-sm flex items-center gap-2"><Plus size={16} />Add Lead</button>
          <label className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm flex items-center gap-2 cursor-pointer hover:bg-[var(--bg-hover)]"><Upload size={16} />Import CSV<input type="file" className="hidden" accept=".csv" onChange={(e) => importCsv(e.target.files?.[0])} /></label>
          <button onClick={exportCsv} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm flex items-center gap-2 hover:bg-[var(--bg-hover)]"><Download size={16} />Export CSV</button>
        </div>
      </div>

      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3 mb-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, title, city, state..." className="input-field md:max-w-md" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-field md:max-w-[220px]">
            <option value="all">All Statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="mt-3 text-sm text-[var(--text-muted)] flex flex-wrap gap-3">
          <span>Total: <b className="text-[var(--text)]">{counts.total || 0}</b></span>
          {STATUSES.map((s) => (
            <span key={s.value}>{s.label}: <b style={{ color: s.color }}>{counts[s.value] || 0}</b></span>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto border border-[var(--border)] rounded-xl">
        <table className="min-w-[1200px] w-full text-sm">
          <thead className="bg-[var(--bg-column)]">
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} className="text-left px-3 py-2 border-b border-[var(--border)] cursor-pointer select-none" onClick={() => onSort(c.key)}>
                  <span className="inline-flex items-center gap-1">{c.label}{sortBy === c.key && (sortDir === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-[var(--text-muted)]">Loading leads...</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-[var(--text-muted)]">No leads found.</td></tr>
            ) : (
              sorted.map((lead) => (
                <tr key={lead.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] cursor-pointer" onClick={() => openEdit(lead)}>
                  <td className="px-3 py-2">{lead.name}</td>
                  <td className="px-3 py-2">{lead.title}</td>
                  <td className="px-3 py-2">{lead.company}</td>
                  <td className="px-3 py-2">{lead.linkedin ? <a href={lead.linkedin} target="_blank" rel="noreferrer" className="text-[var(--blue)] underline" onClick={(e) => e.stopPropagation()}>Profile</a> : ""}</td>
                  <td className="px-3 py-2">{lead.email ? <a href={`mailto:${lead.email}`} className="text-[var(--blue)] underline" onClick={(e) => e.stopPropagation()}>{lead.email}</a> : ""}</td>
                  <td className="px-3 py-2">{lead.phone}</td>
                  <td className="px-3 py-2">{lead.city}</td>
                  <td className="px-3 py-2">{lead.state}</td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <select value={lead.status} onChange={(e) => handleStatusQuick(lead.id, e.target.value as LeadStatus)} className="px-2 py-1 rounded-md border border-[var(--border)]" style={{ color: statusColor(lead.status), backgroundColor: `${statusColor(lead.status)}22` }}>
                      {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">{lead.lastContacted}</td>
                  <td className="px-3 py-2 max-w-[250px] truncate">{lead.notes}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal title="Add Lead" onClose={() => setShowCreate(false)}>
          <LeadForm lead={createForm as LeadItem} onChange={(field, value) => setCreateForm((p) => ({ ...p, [field]: value }))} />
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Btn>
            <Btn onClick={createLead}>Create</Btn>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title={`Lead #${editing.id}`} onClose={() => setEditing(null)} wide>
          <LeadForm lead={editing} onChange={(field, value) => setEditing((p) => (p ? { ...p, [field]: value } : p))} />

          <div className="mt-4 border-t border-[var(--border)] pt-4">
            <h4 className="font-semibold mb-2">Activity Log / Notes</h4>
            <div className="flex gap-2 mb-3">
              <input className="input-field" placeholder="Add timestamped note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
              <Btn onClick={addNote}>Add</Btn>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {notes.map((n) => (
                <div key={n.id} className="text-sm bg-[var(--bg-column)] border border-[var(--border)] rounded-lg px-3 py-2">
                  <div className="text-xs text-[var(--text-muted)] mb-1">{new Date(n.createdAt).toLocaleString()}</div>
                  <div>{n.text}</div>
                </div>
              ))}
              {!notes.length && <div className="text-sm text-[var(--text-muted)]">No activity yet.</div>}
            </div>
          </div>

          <div className="flex justify-between mt-4">
            <Btn variant="danger" onClick={async () => { await leadStore.remove(editing.id); setEditing(null); await loadLeads(); }}><Trash2 size={14} /> Delete</Btn>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}><X size={14} /> Cancel</Btn>
              <Btn onClick={saveEdit}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LeadForm({ lead, onChange }: { lead: LeadItem; onChange: (field: keyof LeadItem, value: string) => void }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Name"><input value={lead.name || ""} onChange={(e) => onChange("name", e.target.value)} className="input-field" /></Field>
      <Field label="Title"><input value={lead.title || ""} onChange={(e) => onChange("title", e.target.value)} className="input-field" /></Field>
      <Field label="Company"><input value={lead.company || ""} onChange={(e) => onChange("company", e.target.value)} className="input-field" /></Field>
      <Field label="Website"><input value={lead.website || ""} onChange={(e) => onChange("website", e.target.value)} className="input-field" /></Field>
      <Field label="LinkedIn"><input value={lead.linkedin || ""} onChange={(e) => onChange("linkedin", e.target.value)} className="input-field" /></Field>
      <Field label="Email"><input value={lead.email || ""} onChange={(e) => onChange("email", e.target.value)} className="input-field" /></Field>
      <Field label="Phone"><input value={lead.phone || ""} onChange={(e) => onChange("phone", e.target.value)} className="input-field" /></Field>
      <Field label="City"><input value={lead.city || ""} onChange={(e) => onChange("city", e.target.value)} className="input-field" /></Field>
      <Field label="State"><input value={lead.state || ""} onChange={(e) => onChange("state", e.target.value)} className="input-field" /></Field>
      <Field label="Status">
        <select value={lead.status || "new"} onChange={(e) => onChange("status", e.target.value)} className="input-field">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Field>
      <Field label="Last Contacted"><input value={lead.lastContacted || ""} onChange={(e) => onChange("lastContacted", e.target.value)} className="input-field" placeholder="YYYY-MM-DD" /></Field>
      <Field label="Notes"><textarea value={lead.notes || ""} onChange={(e) => onChange("notes", e.target.value)} className="input-field" rows={3} /></Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="text-xs text-[var(--text-muted)]">{label}<div className="mt-1">{children}</div></label>;
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 w-full ${wide ? "max-w-4xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Btn({ children, onClick, variant }: { children: React.ReactNode; onClick: () => void; variant?: "ghost" | "danger" }) {
  const cls = variant === "ghost" ? "text-[var(--text-muted)] hover:text-[var(--text)]" : variant === "danger" ? "text-[var(--red)] hover:bg-[var(--red)]/10" : "bg-[var(--accent)] hover:bg-[var(--accent-hover)] font-medium";
  return <button onClick={onClick} className={`px-3 py-2 rounded-lg text-sm transition-colors inline-flex items-center gap-1 ${cls}`}>{children}</button>;
}

function statusColor(status: LeadStatus) {
  return STATUSES.find((s) => s.value === status)?.color || "#a1a1aa";
}

function statusLabel(status: LeadStatus) {
  return STATUSES.find((s) => s.value === status)?.label || "New";
}

function escapeCsv(v: string) {
  const s = String(v || "");
  if (s.includes(",") || s.includes("\n") || s.includes('"')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
